/* Seven Moods — Theme JavaScript */
'use strict';

/* ===== CART DRAWER ===== */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.bindEvents();
    this.updateCount(window.__cartCount || 0);
  }

  bindEvents() {
    document.querySelectorAll('[data-cart-open]').forEach(el =>
      el.addEventListener('click', () => this.open())
    );
    this.overlay?.addEventListener('click', () => this.close());
    this.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('cart:updated', e => this.onCartUpdated(e.detail));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }

  open() {
    this.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    // Use position:fixed scroll-lock instead of overflow:hidden — overflow:hidden
    // breaks touch events on fixed elements in iOS Safari
    this._scrollY = window.scrollY;
    document.body.style.cssText += `;position:fixed;top:-${this._scrollY}px;width:100%;overflow-y:scroll;`;
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflowY = '';
    window.scrollTo(0, this._scrollY || 0);
    this.setAttribute('aria-hidden', 'true');
  }

  async onCartUpdated(cart) {
    this.updateCount(cart.item_count);
    await this.refreshContents();
    this.open();
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  async refreshContents() {
    try {
      const res = await fetch(`${window.location.origin}/cart?section_id=cart-drawer-items`);
      if (!res.ok) return;
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newBody = doc.querySelector('.drawer-body');
      const newFooter = doc.querySelector('.drawer-footer');
      if (newBody) this.querySelector('.drawer-body').replaceWith(newBody);
      if (newFooter) this.querySelector('.drawer-footer').replaceWith(newFooter);
      this.rebindRemoveButtons();
    } catch (err) {
      console.warn('Cart refresh failed:', err);
    }
  }

  rebindRemoveButtons() {
    this.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.removeItem;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          const cart = await res.json();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        } catch (err) {
          console.warn('Remove item failed:', err);
        }
      });
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ===== ADD TO CART ===== */
class AddToCart extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(e) {
    e.preventDefault();
    const btn = this.querySelector('[type="submit"]');
    if (!btn || btn.disabled) return;

    const originalText = btn.textContent.trim();
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(this.form)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add to cart');
      }

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      btn.textContent = '✓ Added';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2200);
    } catch (err) {
      console.error(err);
      btn.textContent = 'Error — try again';
      btn.style.background = '#c0392b';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.style.background = '';
      }, 2500);
    }
  }
}
customElements.define('add-to-cart', AddToCart);

/* ===== VARIANT SELECTOR ===== */
class VariantSelector extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.dataset.variants || '[]');
    this.form = this.closest('form') || document.querySelector('.product-form');

    this.querySelectorAll('.variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.optionGroup;
        this.querySelectorAll(`.variant-btn[data-option-group="${group}"]`).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.onVariantChange();
      });
    });

    this.querySelectorAll('select').forEach(sel =>
      sel.addEventListener('change', () => this.onVariantChange())
    );
  }

  getSelectedOptions() {
    const selected = [];
    this.querySelectorAll('[data-option-index]').forEach(group => {
      const idx = parseInt(group.dataset.optionIndex);
      const active = group.querySelector('.variant-btn.is-selected') || group.querySelector('select');
      if (active) selected[idx] = active.dataset.value || active.value;
    });
    return selected;
  }

  onVariantChange() {
    const options = this.getSelectedOptions();
    const variant = this.variants.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );

    if (!variant) return;

    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl) priceEl.innerHTML = this.formatMoney(variant.price);

    const compareEl = document.querySelector('[data-compare-price]');
    if (compareEl) {
      compareEl.innerHTML = variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : '';
    }

    const submitBtn = this.form?.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.textContent = variant.available
        ? (submitBtn.dataset.availableText || 'Add to Bag')
        : 'Sold Out';
    }

    const idInput = this.form?.querySelector('[name="id"]');
    if (idInput) idInput.value = variant.id;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.featured_image) {
      const mainImg = document.querySelector('.product-gallery__main img');
      if (mainImg) mainImg.src = variant.featured_image.src.replace('_original', '_1200x');
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }
}
customElements.define('variant-selector', VariantSelector);

/* ===== QUANTITY SELECTOR ===== */
class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minusBtn = this.querySelector('[data-qty-minus]');
    this.plusBtn = this.querySelector('[data-qty-plus]');
    this.minusBtn?.addEventListener('click', () => this.change(-1));
    this.plusBtn?.addEventListener('click', () => this.change(1));
  }
  change(delta) {
    const min = parseInt(this.input.min || 1);
    const max = parseInt(this.input.max || 99);
    const newVal = Math.min(Math.max(parseInt(this.input.value) + delta, min), max);
    this.input.value = newVal;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('quantity-selector', QuantitySelector);

/* ===== MOBILE MENU ===== */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    // Open / close whole menu
    document.querySelectorAll('[data-menu-open]').forEach(el =>
      el.addEventListener('click', () => this.open())
    );
    document.querySelectorAll('[data-menu-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    this.querySelector('.mobile-menu__overlay')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    // Submenu accordions — use display toggle, not hidden (hidden gets overridden by CSS)
    this.querySelectorAll('.mobile-menu__link--parent').forEach(btn => {
      const submenu = btn.nextElementSibling;
      if (!submenu) return;
      // Ensure closed on init
      submenu.style.display = 'none';
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', () => {
        const isOpen = submenu.style.display !== 'none';
        submenu.style.display = isOpen ? 'none' : 'block';
        btn.setAttribute('aria-expanded', String(!isOpen));
        btn.classList.toggle('is-open', !isOpen);
      });
    });

    // Mobile search
    const searchForm = this.querySelector('.mobile-menu__search-form');
    searchForm?.addEventListener('submit', e => {
      e.preventDefault();
      const q = searchForm.querySelector('input')?.value.trim();
      if (q) window.location = '/search?q=' + encodeURIComponent(q);
    });
  }
  open() {
    this.classList.add('is-open');
    this._scrollY = window.scrollY;
    document.body.style.cssText += `;position:fixed;top:-${this._scrollY}px;width:100%;overflow-y:scroll;`;
  }
  close() {
    this.classList.remove('is-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflowY = '';
    window.scrollTo(0, this._scrollY || 0);
  }
}
customElements.define('mobile-menu', MobileMenu);

/* product-gallery is registered by main-product.liquid with touch-swipe support */

/* ===== SCROLL ANIMATIONS ===== */
const scrollObserver = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      scrollObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

document.querySelectorAll('[data-animate]').forEach(el => scrollObserver.observe(el));

/* ===== STICKY HEADER ===== */
const siteHeader = document.querySelector('.site-header');
if (siteHeader) {
  const onScroll = () => {
    siteHeader.classList.toggle('is-scrolled', window.scrollY > 60);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ===== NEWSLETTER FORM ===== */
document.querySelector('.newsletter__form')?.addEventListener('submit', async function(e) {
  e.preventDefault();
  const btn = this.querySelector('button[type="submit"]');
  const input = this.querySelector('input[type="email"]');
  const successMsg = document.querySelector('.newsletter__success');
  if (!btn || !input) return;

  const originalText = btn.textContent;
  btn.textContent = '…';
  btn.disabled = true;

  try {
    const formData = new FormData();
    formData.append('contact[email]', input.value);
    formData.append('form_type', 'customer');
    await fetch('/contact#newsletter-form', { method: 'POST', body: formData });

    if (successMsg) {
      this.style.display = 'none';
      successMsg.style.display = 'block';
    } else {
      btn.textContent = '✓ You\'re in!';
    }
  } catch {
    btn.textContent = originalText;
    btn.disabled = false;
  }
});

/* ===== DESKTOP NAV DROPDOWNS ===== */
document.querySelectorAll('.has-dropdown').forEach(item => {
  const btn = item.querySelector(':scope > .header__nav-link--btn');
  if (!btn) return;

  btn.addEventListener('click', () => {
    const isOpen = item.classList.contains('is-open');
    document.querySelectorAll('.has-dropdown.is-open').forEach(el => {
      el.classList.remove('is-open');
      el.querySelector(':scope > .header__nav-link--btn')?.setAttribute('aria-expanded', 'false');
    });
    if (!isOpen) {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });
});

/* subdropdown: hover-driven via CSS, but also support click for touch */
document.querySelectorAll('.has-subdropdown').forEach(item => {
  const btn = item.querySelector('.header__dropdown-link--btn');
  if (!btn) return;
  btn.addEventListener('click', e => {
    e.stopPropagation();
    const isOpen = item.classList.contains('is-open');
    item.closest('.header__dropdown')
      ?.querySelectorAll('.has-subdropdown.is-open')
      .forEach(el => { el.classList.remove('is-open'); el.querySelector('.header__dropdown-link--btn')?.setAttribute('aria-expanded', 'false'); });
    if (!isOpen) {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });
});

document.addEventListener('click', e => {
  if (!e.target.closest('.has-dropdown')) {
    document.querySelectorAll('.has-dropdown.is-open').forEach(el => {
      el.classList.remove('is-open');
      el.querySelector(':scope > .header__nav-link--btn')?.setAttribute('aria-expanded', 'false');
    });
  }
});

document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    document.querySelectorAll('.has-dropdown.is-open, .has-subdropdown.is-open').forEach(el => {
      el.classList.remove('is-open');
      el.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    });
  }
});

/* mobile submenus handled inside MobileMenu.connectedCallback above */

/* ===== CURSOR SPARKLES ===== */
(function() {
  // Skip on touch-only devices
  if (window.matchMedia('(hover: none)').matches) return;

  const SHAPES = ['\u2726', '\u2736', '\u2726', '\u2738', '\u2726'];
  let last = 0;

  document.addEventListener('mousemove', e => {
    const now = Date.now();
    if (now - last < 60) return; // throttle ~16fps
    last = now;

    const el = document.createElement('span');
    el.className = 'cursor-sparkle';
    el.textContent = SHAPES[Math.floor(Math.random() * SHAPES.length)];

    const size   = Math.random() * 10 + 8;
    const angle  = Math.random() * 360;
    const dist   = Math.random() * 35 + 15;
    const tx     = Math.cos(angle * Math.PI / 180) * dist;
    const ty     = Math.sin(angle * Math.PI / 180) * dist;

    el.style.cssText = `
      left:${e.clientX}px;
      top:${e.clientY}px;
      font-size:${size}px;
      --tx:${tx}px;
      --ty:${ty}px;
    `;

    document.body.appendChild(el);
    el.addEventListener('animationend', () => el.remove());
  });
})();

/* ===== BUTTON SPARKLES ===== */
(function() {
  if (window.matchMedia('(hover: none)').matches) return;
  const SHAPES = ['✦', '✶', '✸', '✦', '✶'];

  document.addEventListener('mouseover', function(e) {
    const btn = e.target.closest('.btn');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const count = 5;

    for (let i = 0; i < count; i++) {
      const el = document.createElement('span');
      el.className = 'cursor-sparkle';
      el.textContent = SHAPES[Math.floor(Math.random() * SHAPES.length)];

      // Random point along the button perimeter
      const edge = Math.random();
      let sx, sy;
      if (edge < 0.25)      { sx = Math.random() * rect.width;  sy = 0; }
      else if (edge < 0.5)  { sx = rect.width;                  sy = Math.random() * rect.height; }
      else if (edge < 0.75) { sx = Math.random() * rect.width;  sy = rect.height; }
      else                  { sx = 0;                            sy = Math.random() * rect.height; }

      const size  = Math.random() * 8 + 7;
      const angle = Math.random() * 360;
      const dist  = Math.random() * 30 + 20;
      const tx    = Math.cos(angle * Math.PI / 180) * dist;
      const ty    = Math.sin(angle * Math.PI / 180) * dist;

      el.style.cssText = `
        left:${rect.left + sx}px;
        top:${rect.top + sy}px;
        font-size:${size}px;
        --tx:${tx}px;
        --ty:${ty}px;
        animation-delay:${i * 40}ms;
      `;

      document.body.appendChild(el);
      el.addEventListener('animationend', () => el.remove());
    }
  }, { passive: true });
})();

/* ===== SCROLL STARS ===== */
(function() {
  const SVGS = [
    // 4-point diamond star
    `<svg class="scroll-star" viewBox="0 0 60 60" width="60" height="60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path class="star-path" d="M30 4 L33 27 L56 30 L33 33 L30 56 L27 33 L4 30 L27 27 Z" stroke="#E0147A" stroke-width="1.5"/>
    </svg>`,
    // 8-arm asterisk
    `<svg class="scroll-star" viewBox="0 0 60 60" width="52" height="52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line class="star-path" x1="30" y1="4"  x2="30" y2="56" stroke="#E0147A" stroke-width="1.5"/>
      <line class="star-path" x1="4"  y1="30" x2="56" y2="30" stroke="#E0147A" stroke-width="1.5"/>
      <line class="star-path" x1="9"  y1="9"  x2="51" y2="51" stroke="#E0147A" stroke-width="1.5"/>
      <line class="star-path" x1="51" y1="9"  x2="9"  y2="51" stroke="#E0147A" stroke-width="1.5"/>
    </svg>`,
    // Small tight 4-point star
    `<svg class="scroll-star" viewBox="0 0 40 40" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path class="star-path" d="M20 3 L22 18 L37 20 L22 22 L20 37 L18 22 L3 20 L18 18 Z" stroke="#E0147A" stroke-width="1.5"/>
    </svg>`,
  ];

  // [selector, css position string, svg index]
  // We always attach to the section's PARENT to avoid overflow:hidden clipping
  const ANCHORS = [
    ['.marquee-strip',        'right:2rem;top:0',          0],
    ['.brands-section',       'left:1.5rem;top:3rem',      1],
    ['.product-grid-section', 'right:2rem;top:3rem',       2],
    ['.brand-story',          'right:2rem;top:2rem',       0],
    ['.newsletter',           'left:2rem;top:2rem',        1],
  ];

  document.addEventListener('DOMContentLoaded', () => {
    ANCHORS.forEach(([selector, style, svgIdx]) => {
      const section = document.querySelector(selector);
      if (!section) return;

      // Always attach to parent so overflow:hidden on the section doesn't clip the star
      const parent = section.parentElement;
      if (!parent) return;
      const parentPos = getComputedStyle(parent).position;
      if (parentPos === 'static') parent.style.position = 'relative';

      const wrap = document.createElement('div');
      wrap.className = 'star-deco';
      wrap.setAttribute('aria-hidden', 'true');

      // Calculate offset relative to parent so star appears near the section
      const sectionTop = section.offsetTop;
      wrap.style.cssText = `position:absolute;top:${sectionTop}px;${style};pointer-events:none;z-index:10;overflow:visible;`;
      wrap.innerHTML = SVGS[svgIdx];
      parent.appendChild(wrap);

      // Set stroke-dasharray for draw animation
      requestAnimationFrame(() => {
        wrap.querySelectorAll('.star-path').forEach(path => {
          const len = typeof path.getTotalLength === 'function' ? path.getTotalLength() : 100;
          path.style.strokeDasharray = len;
          path.style.strokeDashoffset = len;
        });
      });

      // Draw when section (not wrap) scrolls into view
      const obs = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            wrap.querySelectorAll('.star-path').forEach((path, i) => {
              path.style.transition = `stroke-dashoffset 0.9s cubic-bezier(.4,0,.2,1) ${i * 0.15}s`;
              path.style.strokeDashoffset = '0';
            });
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.2 });

      obs.observe(section);
    });
  });
})();

/* ===== SMOOTH SCROLL FOR ANCHORS ===== */
document.querySelectorAll('a[href^="#"]').forEach(a => {
  a.addEventListener('click', e => {
    const target = document.querySelector(a.getAttribute('href'));
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
});
